import React, { useEffect, useRef, useState, useContext } from "react";
import { Link } from "react-router-dom";
import { Button, Cascader, Input, Switch, Table, Tooltip, Typography, Modal, Space } from "antd";
import { FilterFilled } from "@ant-design/icons";
import { Icons } from "@design-system";
import { useMatterTemplateQuery } from "../../../hooks/matter-template";
import { MatterTemplateModalContainer } from "./matter-template-modal-container";
import {
  MATTER_TEMPLATE_LIST_ITEMS_QUERY,
  useOrganisationalUnitsQuery,
} from "../../../graphql/query";
import { useMutation } from "@apollo/client";
import { UPDATE_MATTER_TEMPLATES, UPDATE_MATTER_TEMPLATE } from "../../../graphql/mutation";
import { UpdateMatterTemplateMutation, UpdateMatterTemplateMutationVariables } from "~/gql/graphql";
import { CopyOutlined, DownloadOutlined, SaveOutlined } from "@ant-design/icons";
import { SingleValueType } from "rc-cascader/lib/Cascader";
import { useImmer } from "use-immer";
import {
  UpdateManyMatterTemplate,
  UpdateManyMatterTemplateVariables,
} from "../../../graphql/__generated_types__/UpdateManyMatterTemplate";
import { FindMatterTemplateListItems } from "../../../graphql/__generated_types__/FindMatterTemplateListItems";
import { mergeArraysOfObjectsByProp } from "../../../helpers";
import { FindOrganisationalUnitsQuery } from "~/gql/graphql";
import { compareAsc } from "date-fns";
import { TemplateType, FindMatterTemplateListItemsQuery } from "../../../../src/gql/graphql";
import { useRestApi } from "~/hooks";
import { GlobalContext } from "~/context/global-context";
import produce from "immer";
import { Permission, usePermissionCheck } from "../../../hooks";
import { useSessionStorage } from "usehooks-ts";
import { useTaxonomyLabel, VPDLabel } from "~/context/taxonomy-context";

interface Option {
  value: string | number;
  label: string;
  children?: Option[];
}

type FormType = {
  id: number;
  organisationalUnitId: number;
};

export const orgUnitCascaderValue = (
  pa: FindOrganisationalUnitsQuery["organisationalUnits"],
  id?: number | undefined
) => {
  const item = pa.find((p) => p.id === id);

  if (item?.parent) {
    return [item.parent.id, item.id];
  } else {
    return item ? [item.id] : [];
  }
};

type ModalState = {
  show: boolean;
  copiedTemplate?: {
    id: number;
    title: string;
  };
};

const INITIAL_MODAL_STATE = {
  show: false,
};

export const MatterTemplateDataImportContainer = () => {
  const [showCreateModal, setShowCreateModal] = useState<ModalState>(INITIAL_MODAL_STATE);
  const restApi = useRestApi();
  const downloadRef = useRef<HTMLAnchorElement>();
  const [downloadingTemplates, setDownloadingTemplates] = useState(false);

  const taxonomy = useTaxonomyLabel();

  const { data, loading } = useOrganisationalUnitsQuery();
  const [search, setSearchText] = useState<string | null>(null);
  const [templateType, setTemplateType] = useState(TemplateType.Firm);
  const [orderBy, setOrderBy] = useState({ title: "asc" });

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);

  const [orgUnitFilter, setOrgUnitFilter] = useSessionStorage<number | null>(
    "org-unit-filter",
    null
  );
  const [selectedFilterText, setSelectedFilterText] = useSessionStorage<string>(
    "org-unit-filter-text",
    ""
  );

  const [updateTemplates] = useMutation<
    UpdateManyMatterTemplate,
    UpdateManyMatterTemplateVariables
  >(UPDATE_MATTER_TEMPLATES);

  const {
    matterTemplates,
    loading: templatesLoading,
    total,
    refetch,
  } = useMatterTemplateQuery(offset, limit, templateType, orderBy, search, orgUnitFilter);

  const [updateMatterTemplate] = useMutation<
    UpdateMatterTemplateMutation,
    UpdateMatterTemplateMutationVariables
  >(UPDATE_MATTER_TEMPLATE);

  const currentUserInfo = useContext(GlobalContext);

  useEffect(() => {
    setOffset(0);
  }, [search, templateType]);

  const filteredTemplates = matterTemplates ?? [];

  const onDownloadAllTemplates = async () => {
    try {
      setDownloadingTemplates(true);
      const fileDownloader = await restApi.downloadAllTemplates();
      if (downloadRef.current) {
        await fileDownloader.download(downloadRef.current);
      }
    } finally {
      setDownloadingTemplates(false);
    }
  };

  const onClone = (id: number, title: string) => {
    setShowCreateModal(
      produce((draft) => {
        draft.show = true;
        draft.copiedTemplate = {
          id,
          title,
        };
      })
    );
  };

  const onDelete = async (id: number) => {
    await updateMatterTemplate({
      variables: {
        id,
        data: {
          deletedAt: { set: new Date() },
          deletedBy: {
            connect: { id: currentUserInfo?.currentUser?.userId },
          },
        },
      },
    });

    await refetch();
  };

  const onSave = (updates: FormType[]) => {
    updateTemplates({
      variables: {
        input: updates.map((x) => ({
          id: x.id,
          payload: {
            organisationalUnit: { connect: { id: x.organisationalUnitId } },
          },
        })),
      },
      update: (cache, result) => {
        cache.updateQuery<FindMatterTemplateListItems>(
          { query: MATTER_TEMPLATE_LIST_ITEMS_QUERY },
          (currentData) => {
            if (currentData && result.data?.updateManyMatterTemplate) {
              const merged = mergeArraysOfObjectsByProp(
                currentData.matterTemplates.items,
                result.data?.updateManyMatterTemplate,
                "id"
              );

              return {
                matterTemplates: {
                  __typename: "PaginatedMatterTemplates",
                  items: merged,
                  total: currentData.matterTemplates.total,
                },
              };
            }
          }
        );
      },
    });
  };

  const practiceAreas = data;

  const paOptions: Option[] =
    practiceAreas?.organisationalUnits
      .filter((x) => !x.parent?.id)
      .map((pa) => {
        return {
          value: pa.id,
          label: pa.name,
          children: pa.children?.map((y) => ({ value: y.id, label: y.name })) || [],
        };
      }) || [];
  return (
    <>
      {showCreateModal.show && (
        <MatterTemplateModalContainer
          copiedTemplate={showCreateModal.copiedTemplate}
          onCancel={() =>
            setShowCreateModal(
              produce((curr) => {
                curr.show = false;
                curr.copiedTemplate = undefined;
              })
            )
          }
          refetchVariables={{
            offset,
            limit,
            templateType,
            orderBy,
            search,
          }}
        />
      )}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          type="primary"
          style={{ margin: 5, marginLeft: 0 }}
          onClick={() => setShowCreateModal(produce((curr) => void (curr.show = true)))}
        >
          Create Matter {taxonomy.Template}
        </Button>
        <Button
          icon={<DownloadOutlined />}
          loading={downloadingTemplates}
          style={{ margin: 5, marginLeft: 0 }}
          onClick={() => onDownloadAllTemplates()}
        >
          Download All {taxonomy.Template}s
        </Button>
      </div>
      <MatterTemplatesTable
        onClone={onClone}
        total={total ?? 0}
        onNextPage={(offset) => {
          setOffset(offset);
        }}
        practiceAreas={practiceAreas?.organisationalUnits ?? []}
        onSave={onSave}
        setLimit={setLimit}
        paOptions={paOptions}
        paLoading={loading}
        loading={templatesLoading}
        matterTemplates={filteredTemplates}
        search={search || ""}
        onSetSearch={(val) => setSearchText(val)}
        onToggleChange={(checked) =>
          setTemplateType(checked ? TemplateType.Vpd : TemplateType.Firm)
        }
        setOrderBy={setOrderBy}
        onDelete={onDelete}
        selectedFilterText={selectedFilterText}
        setSelectedFilterText={setSelectedFilterText}
        handleOrgUnitFilterChange={(value) => setOrgUnitFilter(value)}
      />
      <a ref={(e) => (downloadRef.current = e || undefined)} />
    </>
  );
};

const MatterTemplatesTable = ({
  loading,
  paLoading,
  matterTemplates,
  paOptions,
  onSave,
  onNextPage,
  setLimit,
  practiceAreas,
  total,
  search,
  onSetSearch,
  onToggleChange,
  onClone,
  onDelete,
  setOrderBy,
  selectedFilterText,
  setSelectedFilterText,
  handleOrgUnitFilterChange,
}: {
  paLoading: boolean;
  loading: boolean;
  matterTemplates: FindMatterTemplateListItemsQuery["matterTemplates"]["items"];
  paOptions: Option[];
  practiceAreas: FindOrganisationalUnitsQuery["organisationalUnits"];
  onNextPage: (offset: number) => void;
  onSave: (updates: FormType[]) => void;
  setLimit: (limit: number) => void;
  total: number;
  search: string;
  onSetSearch: (search: string) => void;
  onToggleChange: (checked: boolean) => void;
  onClone: (id: number, title: string) => void;
  onDelete: (id: number) => Promise<void>;
  setOrderBy: (orderBy: any) => void;
  selectedFilterText: string;
  setSelectedFilterText: (text: string) => void;
  handleOrgUnitFilterChange: (id: number | null) => void;
}) => {
  const restApi = useRestApi();

  const showDelete = usePermissionCheck(Permission.UPDATE_CONFIG);

  const downloadRef = useRef<HTMLAnchorElement>();
  const [downloading, setDownloading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedMatterId, setSelectedMatterId] = useState<number | null>(null);
  const PA_LABEL = VPDLabel("PracticeArea");

  const initialValues = matterTemplates.map((x) => ({
    id: x.id,
    organisationalUnitId: x.organisationalUnit?.id,
  }));

  const [updates, setUpdated] = useImmer<{ id: number; organisationalUnitId: number }[]>([]);

  const handleOnSearch = (searchTerm: string) => {
    if (searchTerm !== search) {
      onSetSearch(searchTerm);
    }
  };

  const onDownloadTemplate = async (templateId: number) => {
    try {
      const fileDownloader = await restApi.getTemplate(templateId);
      if (downloadRef.current) {
        await fileDownloader.download(downloadRef.current);
      }
    } finally {
      setDownloading(false);
    }
  };

  const onOptionChange = (val: SingleValueType, templateId: number) => {
    const target = val[1] ? Number(val[1]) : Number(val[0]);
    const newItem = { id: templateId, organisationalUnitId: Number(target) };

    setUpdated((draft) => {
      const currentIndex = draft.map((x) => x.id).indexOf(newItem.id);
      if (initialValues.find((x) => x.id === templateId)?.organisationalUnitId !== target) {
        currentIndex >= 0 ? (draft[currentIndex] = newItem) : draft.push(newItem);
      } else {
        draft.splice(currentIndex, 1);
      }
    });
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    handleOrgUnitFilterChange(null);
    setSelectedFilterText("");
  };

  return (
    <div>
      <Input.Search
        placeholder="Search by title"
        allowClear={true}
        style={{ marginBottom: 5, width: 500 }}
        onChange={(e: any) => {
          if (e.target.value === "") {
            handleOnSearch(e.target.value);
          }
        }}
        onSearch={(e) => handleOnSearch(e)}
      ></Input.Search>
      <span style={{ marginTop: 10 }}>
        <Switch style={{ marginLeft: 40 }} onChange={(val) => onToggleChange(val)}></Switch>
        <span style={{ paddingLeft: 5 }}>Show VPD Templates</span>
      </span>
      <Table
        key={selectedFilterText} // force table to re-render when filter text changes -- workaround since the filter icon is not updating correctly
        columns={[
          {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: "20rem",
            defaultSortOrder: "ascend",
            sorter: (a, b) => {
              const one = a.title ?? "";
              const two = b.title ?? "";
              return one.localeCompare(two);
            },
          },
          {
            title: () => (
              <span>
                {PA_LABEL}
                {selectedFilterText && (
                  <span style={{ fontSize: 12, marginLeft: 10 }}>{selectedFilterText}</span>
                )}
              </span>
            ),
            dataIndex: ["organisationalUnit", "name"],
            key: "practiceArea",
            width: "25rem",
            filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
            filterDropdown({ clearFilters, selectedKeys, setSelectedKeys, confirm }) {
              return (
                <div style={{ padding: 8 }}>
                  <Cascader
                    onChange={(val) => {
                      setSelectedKeys(val);
                      confirm();
                      if (!val) {
                        handleOrgUnitFilterChange(null);
                        confirm();
                        return;
                      }
                      const id = val[1] ? val[1] : val[0];
                      const selectedName = val[1]
                        ? practiceAreas.flatMap((x) => x.children).find((c) => c?.id === val[1])
                        : practiceAreas.find((x) => x?.id === val[0]);

                      const filterText = selectedName
                        ? selectedName.name
                        : id === 0
                        ? "Firm Wide"
                        : "";

                      setSelectedFilterText(filterText);
                      handleOrgUnitFilterChange(Number(id));
                    }}
                    placeholder={`Select ${PA_LABEL}`}
                    options={paOptions}
                    showSearch={true}
                    value={selectedKeys}
                  />
                  <Space>
                    <Button
                      onClick={() => {
                        clearFilters && handleReset(clearFilters);
                        setSelectedFilterText("");
                        confirm();
                      }}
                      size="small"
                      style={{ width: 70, marginLeft: 3 }}
                    >
                      Reset
                    </Button>
                  </Space>
                </div>
              );
            },
            filtered: selectedFilterText !== "",
            render: (val, record) => {
              if (paOptions.length > 0) {
                return (
                  <Cascader
                    key={record.id}
                    loading={paLoading}
                    onChange={(val: any) => onOptionChange(val, record.id)}
                    defaultValue={orgUnitCascaderValue(
                      practiceAreas,
                      record.organisationalUnit?.id
                    )}
                    placeholder={`Select ${PA_LABEL}`}
                    options={paOptions}
                    showSearch={true}
                    allowClear={false}
                  />
                );
              }
            },
          },
          {
            title: "Type",
            key: "templateType",
            dataIndex: "templateType",
            width: "10rem",
          },
          {
            title: "Last updated",
            dataIndex: "updatedAt",
            key: "updatedAt",
            defaultSortOrder: "ascend",
            filterSearch: true,
            width: "20rem",
            render: (value: string) => new Date(value).toLocaleString(),
            sorter: (a, b) => compareAsc(new Date(a.createdAt), new Date(b.createdAt)),
          },
          {
            title: "Created by",
            dataIndex: ["createdBy", "displayName"],
            key: "createdBy",
            width: "15rem",
          },
          {
            title: "",
            width: "5rem",
            key: "action",
            render: (value, record, i) => (
              <Tooltip key={i} title="Edit Matter Template">
                <Link
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                  }}
                  to={`${record.id}/scope`}
                >
                  {Icons.Edit}
                </Link>
              </Tooltip>
            ),
          },
          ...(showDelete
            ? [
                {
                  key: "delete",
                  width: "5rem",
                  onCell: (record: any) => ({
                    // Prevent row onClick handler from being called
                    onClick: (e: any) => {
                      e.stopPropagation();
                      setSelectedMatterId(record.id);
                      setShowModal(true);
                    },
                  }),
                  render: (value: any, record: any, i: number) => {
                    return (
                      <Tooltip key={i} title="Delete Matter Template">
                        <Typography.Link
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%",
                          }}
                          type="danger"
                        >
                          {Icons.Delete}
                        </Typography.Link>
                      </Tooltip>
                    );
                  },
                },
              ]
            : []),
          {
            title: "",
            width: "5rem",
            key: "action",
            render: (value, record, i) => (
              <Tooltip key={i} title="Clone">
                <Button
                  onClick={() => onClone(record.id, record.title)}
                  icon={<CopyOutlined />}
                ></Button>
              </Tooltip>
            ),
          },
          {
            title: "",
            width: "5rem",
            key: "action",
            render: (value, record, i) => (
              <Tooltip key={i} title="Download Template">
                <Button
                  type="primary"
                  onClick={() => onDownloadTemplate(record.id)}
                  loading={downloading}
                  icon={<DownloadOutlined />}
                ></Button>
              </Tooltip>
            ),
          },
        ]}
        dataSource={matterTemplates}
        loading={loading}
        rowKey="id"
        size="small"
        pagination={{
          showSizeChanger: true,
          showQuickJumper: false,
          hideOnSinglePage: false,
          showPrevNextJumpers: true,
          pageSizeOptions: [10, 20, 30, 40, 50],
          total,
          onChange: (pag, pageSize) => {
            setLimit(pageSize ?? 10);
            const offset = (pag - 1) * pageSize;
            return onNextPage(offset);
          },
        }}
        onChange={(pagination, filters, sorter) => {
          // handle sorting here
          if (!Array.isArray(sorter)) {
            let field;
            if (Array.isArray(sorter.field)) {
              field = sorter.field[0].toString();
            } else {
              field = sorter.field?.toString();
            }
            const order = sorter.order?.toString();
            if (field && order) {
              setOrderBy({ [field]: order === "ascend" ? "asc" : "desc" });
            }
          }
        }}
      />

      <Button
        disabled={updates.length === 0}
        type="primary"
        icon={<SaveOutlined />}
        onClick={() => {
          onSave(updates);
          setUpdated([]);
        }}
      >
        Save
      </Button>
      <a ref={(e) => (downloadRef.current = e || undefined)} />
      <Modal
        title="Delete Template"
        open={showModal}
        onOk={() => {
          if (selectedMatterId) {
            onDelete(selectedMatterId);
          }
          setShowModal(false);
          setSelectedMatterId(null);
        }}
        onCancel={() => {
          setShowModal(false);
          setSelectedMatterId(null);
        }}
        okText="Delete"
      >
        <Typography.Text>Are you sure you want to delete this template?</Typography.Text>
      </Modal>
    </div>
  );
};
